/**
 * Admin — Partner Locations.
 *
 * The retailer stores and trusted installers estimates get shared with, keyed by
 * their Salesforce account id. Shares create these automatically on first send;
 * here an admin can look a location up on trusscore.com ahead of time, pin which
 * Estimator retailer's price book applies, set a TESTING email override, attach
 * user accounts (so those people see the location's shares under "Shared with
 * us"), resync the snapshot, or deactivate.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, Store, HardHat, Loader2, RefreshCw, Link2, Mail, Users, AlertTriangle, ChevronLeft, ChevronRight, Trash2, ExternalLink, CheckCircle2, Plus, X,
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  fetchPartnerLocations, lookupUpstream, createPartnerLocation, patchPartnerLocation, updatePartnerMembers, deletePartnerLocation,
  type PartnerLocation, type PartnerRetailerOption, type UpstreamLocation,
} from '../../services/adminPartnersApi';
import type { MapDatasetName, ShareKind } from '../../types/share';

const fmt = (s: string | null | undefined) => (s ? new Date(s.replace(' ', 'T')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const KindIcon: React.FC<{ kind: ShareKind | string }> = ({ kind }) => (kind === 'retailer' ? <Store size={14} /> : <HardHat size={14} />);

interface Props {
  /** retailer = the Retailer Locations chip; installer = the Trusted Installers page. */
  kind: ShareKind;
}

export const AdminPartners: React.FC<Props> = ({ kind }) => {
  const { isSuperAdmin } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const retailerFilter = kind === 'retailer' && searchParams.get('retailer') ? Number(searchParams.get('retailer')) : null;
  const setRetailerFilter = (id: number | null) => setSearchParams(id ? { retailer: String(id) } : {}, { replace: true });
  const noun = kind === 'retailer' ? 'location' : 'installer';
  const [data, setData] = useState<{ locations: PartnerLocation[]; retailers: PartnerRetailerOption[]; upstreamConfigured: boolean; pagination: { page: number; pages: number; total: number } } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [query, setQuery] = useState<{ search: string; linked: '' | '1' | '0'; override: boolean; page: number }>({ search: '', linked: '', override: false, page: 1 });
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState<PartnerLocation | null>(null);
  const [busy, setBusy] = useState(false);

  // Upstream lookup
  const dataset: MapDatasetName = kind === 'retailer' ? 'retailers' : 'installers';
  const [lk, setLk] = useState<{ ids: string; city: string; region: string; country: string }>({ ids: '', city: '', region: '', country: 'CA' });
  const [lkResults, setLkResults] = useState<UpstreamLocation[] | null>(null);
  const [lkBusy, setLkBusy] = useState(false);
  const [lkNote, setLkNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetchPartnerLocations({ search: query.search, kind, linked: query.linked, override: query.override, page: query.page, perPage: 50, retailerId: retailerFilter });
      setData({ locations: r.locations, retailers: r.retailers, upstreamConfigured: r.upstreamConfigured, pagination: r.pagination });
      if (selected) setSelected(r.locations.find(l => l.id === selected.id) ?? selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load retailers and installers');
    } finally {
      setLoading(false);
    }
  }, [query, selected?.id, kind, retailerFilter]);
  useEffect(() => { void load(); }, [load]);

  const runLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLkBusy(true); setLkNote(null); setLkResults(null);
    try {
      const r = await lookupUpstream({ dataset, ids: lk.ids.trim() || undefined, city: lk.city.trim() || undefined, region: lk.region.trim() || undefined, country: lk.country.trim() || undefined, limit: 100 });
      if (r.notConfigured) { setLkNote('The trusscore.com endpoint is not configured (Settings → Integrations → Location Maps).'); return; }
      setLkResults(r.locations);
      if (r.stale) setLkNote('Showing cached results — trusscore.com did not answer just now.');
    } catch (err) {
      setLkNote(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setLkBusy(false);
    }
  };

  const link = async (u: UpstreamLocation) => {
    setBusy(true); setMessage(null); setError(null);
    try {
      const r = await createPartnerLocation({ sfid: u.sfid || u.id, dataset, id: u.id });
      setMessage(r.existed ? `${r.location.name} was already in the list.` : `${r.location.name} added${r.location.retailerName ? ` → ${r.location.retailerName}` : ''}.`);
      setLkResults(prev => prev ? prev.map(x => x.sfid === u.sfid ? { ...x, linkedId: r.location.id } : x) : prev);
      await load();
      setSelected(r.location);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not link the location');
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id: number, p: Parameters<typeof patchPartnerLocation>[1], okMsg: string) => {
    setBusy(true); setMessage(null); setError(null);
    try {
      const r = await patchPartnerLocation(id, p);
      setSelected(r.location);
      setMessage(okMsg);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (l: PartnerLocation) => {
    const purge = l.shareCount > 0;
    const msg = purge
      ? `PURGE ${l.name}?\n\nThis permanently deletes the ${noun} AND its ${l.shareCount} share(s) — every private conversation, event-log entry and promotion with them. The customers' original estimates are untouched. This cannot be undone and is meant for testing.\n\nContinue?`
      : `Delete ${l.name}? It has never been shared with, so nothing else is affected.`;
    if (!window.confirm(msg)) return;
    if (purge && !window.confirm(`Last check — delete ${l.shareCount} share(s) to ${l.name} for good?`)) return;
    setBusy(true); setMessage(null); setError(null);
    try {
      const r = await deletePartnerLocation(l.id, purge);
      setSelected(null);
      setMessage(purge ? `${l.name} and ${r.purgedShares} share(s) deleted.` : `${l.name} deleted.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const filterRetailerName = retailerFilter ? data?.retailers.find(r => r.id === retailerFilter)?.name ?? `#${retailerFilter}` : null;

  return (
    <div>
      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        {kind === 'retailer' ? (
          <>
            <h4 className="fw-bold mb-1 d-flex align-items-center gap-2"><Store size={20} /> Retailer Locations</h4>
            <p className="text-muted small mb-0">
              The physical stores on the trusscore.com map that customers send estimates to. Each location belongs to one
              <strong> retailer</strong>, whose price book prices the estimates sent to it and whose users see them. A location is
              linked automatically from the retailer&apos;s map filter, or pinned here. Locations also appear here by themselves the
              first time a customer shares with them.
            </p>
          </>
        ) : (
          <>
            <h4 className="fw-bold mb-1 d-flex align-items-center gap-2"><HardHat size={20} /> Trusted Installers</h4>
            <p className="text-muted small mb-0">
              The trusted installers on the trusscore.com map that customers send estimates to. Installers have no price book
              (they receive quantities only). User accounts can be attached so those people see the installer&apos;s shares under
              &ldquo;Shared with us&rdquo;. Installers appear here by themselves the first time a customer shares with them.
            </p>
          </>
        )}
      </div>

      {/* Lookup */}
      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2"><Search size={18} /> Find {kind === 'retailer' ? 'a location' : 'an installer'} on trusscore.com</h5>
        <p className="text-muted small mb-2">
          Search the trusscore.com map and link {kind === 'retailer' ? 'a location' : 'an installer'} ahead of time.
          {data && !data.upstreamConfigured && <span className="text-danger"> The upstream endpoint is not configured.</span>}
        </p>
        <form className="d-flex flex-wrap gap-2 align-items-end" onSubmit={runLookup}>
          <div><label className="form-label small fw-semibold mb-1">Salesforce / public ids</label><input className="form-control form-control-sm" placeholder="comma-separated" value={lk.ids} onChange={e => setLk(s => ({ ...s, ids: e.target.value }))} style={{ width: 220 }} /></div>
          <div><label className="form-label small fw-semibold mb-1">City</label><input className="form-control form-control-sm" value={lk.city} onChange={e => setLk(s => ({ ...s, city: e.target.value }))} /></div>
          <div><label className="form-label small fw-semibold mb-1">Region</label><input className="form-control form-control-sm" placeholder="ON, Ontario…" value={lk.region} onChange={e => setLk(s => ({ ...s, region: e.target.value }))} style={{ width: 120 }} /></div>
          <div><label className="form-label small fw-semibold mb-1">Country</label><input className="form-control form-control-sm" maxLength={2} value={lk.country} onChange={e => setLk(s => ({ ...s, country: e.target.value.toUpperCase() }))} style={{ width: 70 }} /></div>
          <button type="submit" className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1" disabled={lkBusy}>{lkBusy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search</button>
        </form>
        {lkNote && <div className="alert alert-warning py-2 small mt-2 mb-0">{lkNote}</div>}
        {lkResults && (
          <div className="table-responsive mt-2" style={{ maxHeight: 320 }}>
            <table className="table table-sm table-hover mb-0 small">
              <thead><tr><th>Name</th><th>Where</th><th>Email</th><th>sfid</th><th /></tr></thead>
              <tbody>
                {lkResults.length === 0 && <tr><td colSpan={5} className="text-muted">No locations found.</td></tr>}
                {lkResults.map(u => (
                  <tr key={u.id}>
                    <td><KindIcon kind={u.type} /> {u.name}</td>
                    <td>{[u.city, u.region].filter(Boolean).join(', ')}</td>
                    <td>{u.email || <span className="text-muted">—</span>}</td>
                    <td className="font-monospace">{u.sfid || u.id}</td>
                    <td className="text-end">
                      {u.linkedId
                        ? <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={() => { const l = data?.locations.find(x => x.id === u.linkedId); if (l) setSelected(l); else setSearchInput(u.sfid); }}><CheckCircle2 size={13} /> Linked</button>
                        : isSuperAdmin && <button type="button" className="btn btn-sm btn-primary py-0" disabled={busy} onClick={() => void link(u)}><Link2 size={13} /> Link</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {message && <div className="alert alert-success py-2 small">{message}</div>}
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <div className="row g-3">
        {/* List */}
        <div className="col-12 col-lg-7">
          <div className="bg-white rounded-3 shadow-sm p-3">
            <form className="d-flex flex-wrap gap-2 align-items-center mb-2" onSubmit={e => { e.preventDefault(); setQuery(q => ({ ...q, search: searchInput.trim(), page: 1 })); }}>
              <div className="input-group input-group-sm" style={{ maxWidth: 320 }}>
                <span className="input-group-text"><Search size={14} /></span>
                <input className="form-control" placeholder="Name, city, email, sfid…" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
              </div>
              <button type="submit" className="btn btn-sm btn-outline-primary">Search</button>
              {kind === 'retailer' && (
                <>
                  <select className="form-select form-select-sm" style={{ width: 'auto', maxWidth: 260 }} value={retailerFilter ?? ''} onChange={e => { setRetailerFilter(e.target.value ? Number(e.target.value) : null); setQuery(q => ({ ...q, page: 1 })); }}>
                    <option value="">All retailers</option>
                    {(data?.retailers ?? []).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 'auto' }} value={query.linked} onChange={e => setQuery(q => ({ ...q, linked: e.target.value as '' | '1' | '0', page: 1 }))}>
                    <option value="">Linked or not</option><option value="1">Linked to a retailer</option><option value="0">Not linked to any retailer</option>
                  </select>
                </>
              )}
              <div className="form-check form-switch mb-0">
                <input id="pl-override" type="checkbox" className="form-check-input" role="switch" checked={query.override} onChange={e => setQuery(q => ({ ...q, override: e.target.checked, page: 1 }))} />
                <label htmlFor="pl-override" className="form-check-label small text-muted">With email override</label>
              </div>
            </form>
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0 small">
                <thead className="table-tc-slate"><tr><th>{kind === 'retailer' ? 'Location' : 'Installer'}</th><th>Email</th>{kind === 'retailer' && <th>Retailer</th>}<th className="text-center text-white" title="Users at this location"><Users size={13} /></th><th className="text-center text-white">Shares</th></tr></thead>
                <tbody>
                  {loading ? <tr><td colSpan={5} className="text-center text-muted py-3">Loading…</td></tr>
                    : !data || data.locations.length === 0 ? <tr><td colSpan={5} className="text-center text-muted py-3">{filterRetailerName ? `No location is linked to ${filterRetailerName} yet — search above and link one, or add the location's Salesforce id to the retailer's map filter.` : `No ${noun}s yet — search above, or they appear here after a customer's first share.`}</td></tr>
                    : data.locations.map(l => (
                      <tr key={l.id} className={selected?.id === l.id ? 'table-active' : ''} style={{ cursor: 'pointer', opacity: l.isActive ? 1 : 0.6 }} onClick={() => setSelected(l)}>
                        <td><div className="fw-semibold d-flex align-items-center gap-1"><KindIcon kind={l.kind} /> {l.name}{!l.isActive && <span className="badge bg-secondary ms-1">inactive</span>}</div><div className="text-muted" style={{ fontSize: '0.8em' }}>{[l.city, l.region].filter(Boolean).join(', ')}</div></td>
                        <td>{l.email || <span className="text-muted">—</span>}{l.emailOverride && <div className="text-danger" style={{ fontSize: '0.8em' }}><Mail size={11} /> → {l.emailOverride}</div>}</td>
                        {kind === 'retailer' && <td>{l.retailerId ? <>{l.retailerName}{l.retailerActive === false && <span className="text-warning"> (paused)</span>}</> : <span className="text-warning">unassigned</span>}</td>}
                        <td className="text-center">{l.memberCount || <span className="text-muted">0</span>}</td>
                        <td className="text-center">{l.activeShareCount}{l.shareCount > l.activeShareCount && <span className="text-muted"> / {l.shareCount}</span>}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {data && data.pagination.pages > 1 && (
              <div className="d-flex justify-content-between align-items-center pt-2 small">
                <span className="text-muted">{data.pagination.total} {noun}s</span>
                <div className="btn-group btn-group-sm">
                  <button type="button" className="btn btn-outline-secondary" disabled={query.page <= 1} onClick={() => setQuery(q => ({ ...q, page: q.page - 1 }))}><ChevronLeft size={14} /></button>
                  <span className="btn btn-outline-secondary disabled">{query.page} / {data.pagination.pages}</span>
                  <button type="button" className="btn btn-outline-secondary" disabled={query.page >= data.pagination.pages} onClick={() => setQuery(q => ({ ...q, page: q.page + 1 }))}><ChevronRight size={14} /></button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="col-12 col-lg-5">
          {selected ? (
            <PartnerEditor key={selected.id} location={selected} retailers={data?.retailers ?? []} canEdit={isSuperAdmin} busy={busy}
              onPatch={(p, msg) => void patch(selected.id, p, msg)} onDelete={() => void remove(selected)} onClose={() => setSelected(null)}
              onMembers={async (change) => {
                setBusy(true); setMessage(null); setError(null);
                try {
                  const r = await updatePartnerMembers(selected.id, change);
                  setSelected(r.location);
                  if (r.unknownEmails.length) setError(`No Estimator account for: ${r.unknownEmails.join(', ')} — they need to sign in once first.`);
                  else setMessage('Members updated.');
                  await load();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Could not update members');
                } finally {
                  setBusy(false);
                }
              }} />
          ) : (
            <div className="bg-white rounded-3 shadow-sm p-4 text-muted small">{kind === 'retailer' ? 'Select a location to change which retailer it belongs to, set a testing email override, or attach accounts by hand.' : 'Select an installer to set a testing email override or attach accounts.'}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const PartnerEditor: React.FC<{
  location: PartnerLocation; retailers: PartnerRetailerOption[]; canEdit: boolean; busy: boolean;
  onPatch: (p: Parameters<typeof patchPartnerLocation>[1], msg: string) => void;
  onMembers: (change: { add?: string[]; remove?: number[] }) => Promise<void>;
  onDelete: () => void; onClose: () => void;
}> = ({ location: l, retailers, canEdit, busy, onPatch, onMembers, onDelete, onClose }) => {
  const [override, setOverride] = useState(l.emailOverride ?? '');
  const [retailerId, setRetailerId] = useState<string>(l.retailerId ? String(l.retailerId) : '');
  const [memberEmail, setMemberEmail] = useState('');
  const dirtyOverride = override.trim() !== (l.emailOverride ?? '');
  return (
    <div className="bg-white rounded-3 shadow-sm p-3">
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div>
          <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2"><KindIcon kind={l.kind} /> {l.name}</h5>
          <div className="text-muted small">{[l.street, l.city, l.region, l.postalCode].filter(Boolean).join(', ')}</div>
          <div className="text-muted small font-monospace">sfid {l.sfid}{l.publicId && l.publicId !== l.sfid ? ` · id ${l.publicId}` : ''}</div>
        </div>
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
      </div>
      <dl className="row small mb-3">
        <dt className="col-4">API email</dt><dd className="col-8">{l.email || <span className="text-muted">none on file</span>}</dd>
        <dt className="col-4">Phone</dt><dd className="col-8">{l.phone || '—'}</dd>
        <dt className="col-4">Profile</dt><dd className="col-8">{l.profileUrl ? <a href={l.profileUrl} target="_blank" rel="noopener noreferrer">trusscore.com <ExternalLink size={11} /></a> : '—'}</dd>
        <dt className="col-4">Shares</dt><dd className="col-8">{l.activeShareCount} active / {l.shareCount} total{l.lastSharedAt && ` · last ${fmt(l.lastSharedAt)}`} · <Link to="/admin/shares">tracking</Link></dd>
        <dt className="col-4">Synced</dt><dd className="col-8">{fmt(l.lastSyncedAt)} {canEdit && <button type="button" className="btn btn-link btn-sm p-0 ms-1" disabled={busy} onClick={() => onPatch({ resync: true }, 'Resynced from trusscore.com.')}><RefreshCw size={12} /> resync</button>}</dd>
      </dl>

      {l.kind === 'retailer' && (
        <div className="mb-3">
          <label className="form-label small fw-semibold mb-1">Retailer this location belongs to (price book, branding, users)</label>
          <div className="d-flex gap-2">
            <select className="form-select form-select-sm" value={retailerId} disabled={!canEdit || busy} onChange={e => setRetailerId(e.target.value)}>
              <option value="">— none (quantities only) —</option>
              {retailers.map(r => <option key={r.id} value={r.id}>{r.name}{!r.isActive ? ' (paused)' : ''}</option>)}
            </select>
            {canEdit && <button type="button" className="btn btn-sm btn-primary text-nowrap" disabled={busy || (retailerId === (l.retailerId ? String(l.retailerId) : ''))} onClick={() => onPatch({ retailerId: retailerId === '' ? null : Number(retailerId) }, 'Assignment saved.')}>Assign</button>}
          </div>
          <div className="form-text">
            {l.retailerLinkMode === 'manual' ? 'Assigned by an admin.' : 'Unassigned: estimates sent here show quantities only.'}
          </div>
        </div>
      )}

      <div className="mb-3">
        <label className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1"><Mail size={13} /> Email override <span className="badge text-bg-danger">testing only</span></label>
        <div className="input-group input-group-sm">
          <input type="email" className="form-control" placeholder="leave blank in production" value={override} disabled={!canEdit || busy} onChange={e => setOverride(e.target.value)} />
          {canEdit && <button type="button" className="btn btn-primary" disabled={busy || !dirtyOverride} onClick={() => onPatch({ emailOverride: override.trim() }, override.trim() ? 'Override set — sends to this location go to it via SMTP.' : 'Override cleared.')}>Save</button>}
          {canEdit && l.emailOverride && <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => { setOverride(''); onPatch({ emailOverride: '' }, 'Override cleared.'); }}><X size={13} /></button>}
        </div>
        <div className="form-text">While set, every share to this {l.kind === 'retailer' ? 'location' : 'installer'} is delivered here <strong>via this server&apos;s SMTP</strong> (Pardot is not contacted), even when the global redirect is on — this override wins. The intended address is still recorded on the share.</div>
      </div>

      <div className="mb-3">
        <label className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1"><Users size={13} /> Accounts attached by hand</label>
        <p className="form-text mt-0 mb-2">{l.kind === 'retailer' ? 'Retail users belong here through their retailer (Retail Users chip). This list is the manual extra: any Estimator account attached here also sees this location\'s shares and can reply as it.' : 'Estimator accounts attached here see this installer\'s shares under "Shared with us" and can reply as the installer.'} The person must have signed in at least once.</p>
        {l.members.length === 0 ? <div className="text-muted small mb-2">None attached by hand.</div> : (
          <ul className="list-group list-group-flush small mb-2">
            {l.members.map(m => (
              <li key={m.userId} className="list-group-item px-0 py-1 d-flex justify-content-between align-items-center">
                <span>{m.email}{m.name && <span className="text-muted"> · {m.name}</span>}</span>
                {canEdit && <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" disabled={busy} onClick={() => void onMembers({ remove: [m.userId] })}><Trash2 size={12} /></button>}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <form className="input-group input-group-sm" onSubmit={e => { e.preventDefault(); if (memberEmail.trim()) { void onMembers({ add: [memberEmail.trim()] }); setMemberEmail(''); } }}>
            <input type="email" className="form-control" placeholder="account email" value={memberEmail} disabled={busy} onChange={e => setMemberEmail(e.target.value)} />
            <button type="submit" className="btn btn-outline-primary" disabled={busy || !memberEmail.trim()}><Plus size={13} /> Attach</button>
          </form>
        )}
      </div>

      {canEdit && (
        <div className="d-flex flex-wrap gap-2 align-items-center border-top pt-2">
          <div className="form-check form-switch mb-0">
            <input id="pl-active" type="checkbox" className="form-check-input" role="switch" checked={l.isActive} disabled={busy} onChange={e => onPatch({ isActive: e.target.checked }, e.target.checked ? 'Location activated.' : 'Location deactivated — new shares to it are refused.')} />
            <label htmlFor="pl-active" className="form-check-label small">Active</label>
          </div>
          <button type="button" className="btn btn-sm btn-outline-danger ms-auto d-inline-flex align-items-center gap-1" disabled={busy} title={l.shareCount > 0 ? `Purge: deletes this ${l.kind} and its ${l.shareCount} share(s) — testing reset` : 'Delete'} onClick={onDelete}><Trash2 size={13} /> {l.shareCount > 0 ? `Purge (${l.shareCount} shares)` : 'Delete'}</button>
        </div>
      )}
      {!l.isActive && <div className="alert alert-warning py-1 small mt-2 mb-0 d-flex align-items-center gap-1"><AlertTriangle size={13} /> Inactive: existing links keep working; new shares are refused.</div>}
    </div>
  );
};

export default AdminPartners;
